import { Card, CardContent, Chip, Link, Stack, Typography } from '@mui/material'
import React from 'react'
import { LabResultProps } from '../types'
import { LabResultRange } from './LabResultRange'
import { getResultThresholds } from './LabResultRange/utils/getResultThresholds'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { useNavigation } from 'src/hooks'

export const NumericalLabResult = ({ labResult }: LabResultProps) => {
    const { navigate } = useNavigation()
    const referenceRange = React.useMemo(
        () => getResultThresholds(labResult.referenceRange),
        [labResult.referenceRange]
    )

    const getChipColor = () => {
        if (!labResult.referenceRange?.text) {
            return 'warning'
        }

        const { valueQuantity } = labResult
        const { low, high } = labResult.referenceRange

        if (!valueQuantity) {
            return 'primary'
        }

        if (high?.value && valueQuantity > high.value) {
            return 'warning'
        }
        if (low?.value && valueQuantity < low.value) {
            return 'warning'
        }

        return 'primary'
    }

    return (
        <Link
            component="div"
            onClick={() =>
                navigate(`/your-care/lab-results/history/${labResult.testCode}`)
            }
            sx={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'block',
                mt: 2
            }}
        >
            <Card
                elevation={0}
                sx={{
                    mt: 2,
                    border: 1,
                    borderColor: 'divider',
                    cursor: 'pointer'
                }}
            >
                <CardContent sx={{ px: 4 }}>
                    {/* Result Section */}
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="start"
                        justifyContent="space-between"
                    >
                        <Stack sx={{ width: '100%' }}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 1, fontWeight: 'normal' }}
                                >
                                    {labResult.testName}
                                </Typography>
                                <ArrowForwardIcon color="action" />
                            </Stack>

                            {labResult.referenceRange?.text && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Normal range:{' '}
                                    {labResult.referenceRange.text}
                                    {labResult.unit ? ` ${labResult.unit}` : ''}
                                </Typography>
                            )}

                            {
                                // If there is a low and high value in the reference range, show the range slider
                                referenceRange ? (
                                    <LabResultRange
                                        min={referenceRange[0]}
                                        max={referenceRange[1]}
                                        valueQuantity={labResult.valueQuantity}
                                    />
                                ) : (
                                    // If there is no low and high value in the reference range, show the value quantity
                                    // in a chip
                                    <Chip
                                        label={`${labResult.valueQuantity} - ${labResult.interpretation}`}
                                        color={getChipColor()}
                                        size="small"
                                        sx={{
                                            mt: 1,
                                            width: 'fit-content',
                                            px: 1,
                                            py: 2
                                        }}
                                    />
                                )
                            }
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        </Link>
    )
}
